import { Divider, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { bbfcRatingOptions, europeanFskRatingOptions, mpaaRatingOptions } from '@/components/SeriesContainer/SeriesConstants';
import VNColorPicker from '@/components/VNColorPicker/VNColorPicker';
import VNTagAutocomplete from '@/components/VNTagAutocomplete/VNTagAutocomplete';
import VNSection from '@/components/VNSection/VNSection';
import VNSelect from '@/components/VNSelect/VNSelect';
import VNTextField from '@/components/VNTextField/VNTextField';
import { Permission } from '@/enums';
import AssetVariantList from '@/pages/assets/asset-fields-widget/AssetVariantList';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';

const PROGRAM_TRIGGER_CATEGORIES = ['Movie', 'Episode', 'Season', 'Series'];

interface Props {
  isMultiple?: boolean;
  isNew?: boolean;
  isMovie?: boolean;
  lockAccessControls?: boolean;
}
const Metadata = ({ isMultiple, isNew, isMovie = false, lockAccessControls = false }: Props) => {
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const isSuperAdmin = useMemo(() => permissions && permissions.has(Permission.OUSuperAdmin), [permissions]);
  const { ouData } = useSelector(applicationDataSelector);

  const assetForm = useFormContext();
  const { watch, getValues } = assetForm;
  const schemaType = watch('schemaType');
  const sourceWatch = watch('source');
  const watchedCategories = watch('common.categories');

  const hasTriggerCategory = (categoryIds: string[]): boolean => {
    if (!categoryIds?.length || !ouData?.categories) return false;
    return ouData.categories
      .filter((cat) => categoryIds.includes(cat.id))
      .some((cat) => PROGRAM_TRIGGER_CATEGORIES.includes(cat.name));
  };

  let cachedSectionStates;
  if (isNew) {
    const prevState = sessionStorage.getItem('accordianStates') || '{"accordian": [false, false, false, false]}';
    cachedSectionStates = JSON.parse(prevState).accordian;
  } else {
    const initialCategories = getValues('common.categories') || [];
    cachedSectionStates = [hasTriggerCategory(initialCategories), false, false, false];
  }

  const [sectionStates, setSectionStates] = useState(cachedSectionStates);

  // Track whether trigger categories were present in the previous render so we
  // only act on actual transitions (none→some = expand, some→none = collapse).
  const prevHasTriggerRef = useRef(hasTriggerCategory(getValues('common.categories') || []));

  useEffect(() => {
    const nowHasTrigger = hasTriggerCategory(watchedCategories || []);
    const prevHasTrigger = prevHasTriggerRef.current;
    prevHasTriggerRef.current = nowHasTrigger;

    if (!prevHasTrigger && nowHasTrigger) {
      // First trigger category added → expand Program Tags
      setSectionStates((prev) => [true, prev[1], prev[2], prev[3]]);
    } else if (prevHasTrigger && !nowHasTrigger) {
      // Last trigger category removed → collapse Program Tags
      setSectionStates((prev) => [false, prev[1], prev[2], prev[3]]);
    }
    // No transition (trigger state unchanged) → leave section state alone
  }, [watchedCategories]);

  return (
    <div>
      <VNSection
        name="Program"
        label="Program Tags"
        variant="dropdown"
        isOpen={sectionStates[0]}
        toggleCallback={() => toggleCallback(0)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNTagAutocomplete
            name="common.series"
            label="Series Name"
            options={watch('common.series')?.map((series: string) => ({ label: series, value: series }))}
          />
          <VNTagAutocomplete
            name="common.season"
            label="Season Number"
            options={watch('common.season')?.map((season: string) => ({ label: season, value: season }))}
          />
          <VNTextField name="common.episode" label="Episode Number" disabled={isMultiple} />
          <VNTagAutocomplete
            name="common.cast"
            label="Cast"
            options={watch('common.cast')?.map((cast: string) => ({ label: cast, value: cast }))}
          />
          <VNTagAutocomplete
            name="common.producers"
            label="Producers"
            options={watch('common.producers')?.map((producer: string) => ({ label: producer, value: producer }))}
          />
          <VNTagAutocomplete
            name="common.directors"
            label="Director(s)"
            options={watch('common.directors')?.map((director: string) => ({ label: director, value: director }))}
          />
          <VNTagAutocomplete
            name="common.genres"
            label="Genres"
            options={watch('common.genres')?.map((genre: string) => ({ label: genre, value: genre }))}
          />
          <VNTextField name="common.programType" label="Program Type" />
          <VNTextField
            name="common.releaseYear"
            type="number"
            label="Release Year"
            required={isMovie}
            rules={
              isMovie
                ? {
                    required: 'Release Year is required',
                    min: { value: 1888, message: 'Value must be a valid year' },
                    max: { value: 2200, message: 'Value must be a valid year' },
                  }
                : {
                    min: undefined,
                    validate: (value) => {
                      if (value === '' || value == null) return true;
                      const n = Number(value);
                      if (n < 1888 || n > 2200) return 'Value must be a valid year';
                      return true;
                    },
                  }
            }
          />
          {isMovie && <VNTextField name="common.highlightMessage" label="Highlight Message" rows={4} />}
          {isMovie && <VNColorPicker name="displaySettings.backgroundColor" label="Background Color" />}
          {isMovie && (
            <VNTextField
              name="accessSettings.demographic.ageGate"
              label="Age Gate"
              type="number"
              disabled={lockAccessControls}
              rules={{
                min: undefined,
                max: undefined,
                validate: (value) => {
                  if (value === '' || value == null) return true;
                  const n = Number(value);
                  if (Number.isNaN(n) || n < 0) return 'Value must be a positive number';
                  if (n > 100) return 'Value must less than 100';
                  return true;
                },
              }}
            />
          )}
          <VNSelect name="common.mpaaRating" label="MPAA Rating" options={mpaaRatingOptions} />
          <VNSelect name="common.bbfcRating" label="BBFC Rating" options={bbfcRatingOptions} />
          <VNSelect name="common.usParentalRatings" label="US Parental Ratings" options={bbfcRatingOptions} />
          <VNSelect name="common.europeanFSKRating" label="European FSK Rating" options={europeanFskRatingOptions} />
        </div>
      </VNSection>

      <VNSection
        name="Sports"
        label="Sports Tags"
        variant="dropdown"
        isOpen={sectionStates[1]}
        toggleCallback={() => toggleCallback(1)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNTagAutocomplete
            name="common.teamName"
            label="Team Names"
            options={watch('common.teamName')?.map((team: string) => ({ label: team, value: team }))}
          />
          <VNTagAutocomplete
            name="common.leagueName"
            label="League names"
            options={watch('common.leagueName')?.map((league: string) => ({ label: league, value: league }))}
          />
        </div>
      </VNSection>

      <VNSection
        name="Network"
        label="Network Tags"
        variant="dropdown"
        isOpen={sectionStates[2]}
        toggleCallback={() => toggleCallback(2)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNTextField name="common.networkValue" label="Network Value" />
          <VNTextField name="common.tmsidEpisode" label="TMSID Episode" />
          <VNTextField name="common.tmsidSeries" label="TMSID Series" />
        </div>
      </VNSection>
      <VNSection
        name="Advanced"
        label="Advanced"
        variant="dropdown"
        isOpen={sectionStates[3]}
        toggleCallback={() => toggleCallback(3)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNTextField name="markers" disabled label="Midroll Ad Cue Points" />
          {schemaType === 'Video' && <VNTextField name="subTitle" label="Sub-title" />}
        </div>
        {isSuperAdmin && !isNew && schemaType === 'Video' && sourceWatch !== 'NestedChannel' && sourceWatch !== 'ClipGroup' && (
          <Grid item xs={12} className="variants-container">
            <Divider />
            <Typography component="div" sx={{ marginTop: 2, marginBottom: 1 }}>
              Variants
            </Typography>
            <AssetVariantList assetFormValues={assetForm} />
          </Grid>
        )}
      </VNSection>
    </div>
  );
  // function useSectionHasValues(names: string[]) {
  //   let result = false;
  //   names.forEach((name) => {
  //     const value = getValues(name);
  //     if (value !== null && value !== undefined && value !== '') {
  //       result = true;
  //     }
  //   });
  //   return result;
  //   // const values = useWatch({ name: names });
  //   // return values.some((value) => {
  //   //   if (Array.isArray(value)) return value.length > 0;
  //   //   return value !== null && value !== undefined && value !== '';
  //   // });
  // }
  function toggleCallback(index: number) {
    const newStates = [...sectionStates];
    newStates[index] = !newStates[index];
    setSectionStates(newStates);
    if (isNew) {
      sessionStorage.setItem('accordianStates', JSON.stringify({ accordian: newStates }));
    }
  }
};

export default Metadata;
